import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Settings as SettingsIcon,
  Link,
  Unlink,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { handleApiError } from '../utils/errorHandler';
import { crmAPI } from '../utils/api';

const crmSettingsSchema = yup.object().shape({
  crmProvider: yup.string().required('CRM provider is required'),
  apiKey: yup.string().required('API key is required'),
  isActive: yup.boolean(),
});

const CRMSettings = () => {
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [crmSettings, setCrmSettings] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(crmSettingsSchema),
    defaultValues: {
      crmProvider: '',
      apiKey: '',
      isActive: false,
    },
  });

  const watchedProvider = watch('crmProvider');

  useEffect(() => {
    fetchCRMSettings();
  }, []);

  const fetchCRMSettings = async () => {
    try {
      setLoading(true);
      const response = await crmAPI.getSettings();
      const settings = response.data;
      setCrmSettings(settings);
      reset({
        crmProvider: settings.crmProvider || '',
        apiKey: settings.apiKey || '',
        isActive: settings.isActive || false,
      });
      setConnectionStatus(settings.connectionStatus);
    } catch (error) {
      if (error.response?.status !== 404) {
        handleApiError(error, 'Failed to fetch CRM settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    const formData = watch();
    if (!formData.crmProvider || !formData.apiKey) {
      toast.error('Please select a CRM provider and enter an API key');
      return;
    }

    try {
      setTesting(true);
      const response = await crmAPI.testConnection({
        crmProvider: formData.crmProvider,
        apiKey: formData.apiKey,
      });

      if (response.data.success) {
        setConnectionStatus('connected');
        toast.success('CRM connection successful!');
      } else {
        setConnectionStatus('failed');
        toast.error('CRM connection failed: ' + response.data.message);
      }
    } catch (error) {
      setConnectionStatus('failed');
      handleApiError(error, 'Failed to test CRM connection');
    } finally {
      setTesting(false);
    }
  };

  const onSubmit = async data => {
    try {
      setLoading(true);
      const response = await crmAPI.updateSettings(data);
      setCrmSettings(response.data);
      setConnectionStatus(response.data.connectionStatus);
      toast.success('CRM settings updated successfully');
    } catch (error) {
      handleApiError(error, 'Failed to update CRM settings');
    } finally {
      setLoading(false);
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Unlink className="h-5 w-5 text-gray-400" />;
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'failed':
        return 'Connection Failed';
      default:
        return 'Not Connected';
    }
  };

  const crmProviders = [
    { value: '', label: 'Select CRM Provider' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'Capsule', label: 'Capsule CRM' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <div className="bg-blue-100 rounded-full p-2 mr-3">
            <Link className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">CRM Integration</h2>
            <p className="text-gray-600">Connect your CRM to sync customer data</p>
          </div>
        </div>
      </div>

      {/* Connection Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getConnectionStatusIcon()}
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Connection Status</h3>
              <p className="text-sm text-gray-600">{getConnectionStatusText()}</p>
            </div>
          </div>
          {crmSettings?.isActive && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              Active
            </span>
          )}
        </div>
      </div>

      {/* CRM Settings Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* CRM Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">CRM Provider</label>
            <select
              {...register('crmProvider')}
              className={`form-input ${errors.crmProvider ? 'form-input-error' : ''}`}
            >
              {crmProviders.map(provider => (
                <option key={provider.value} value={provider.value}>
                  {provider.label}
                </option>
              ))}
            </select>
            {errors.crmProvider && <p className="error-message">{errors.crmProvider.message}</p>}
          </div>

          {/* API Key */}
          {watchedProvider && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  {...register('apiKey')}
                  className={`form-input pr-10 ${errors.apiKey ? 'form-input-error' : ''}`}
                  placeholder="Enter your API key"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showApiKey ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.apiKey && <p className="error-message">{errors.apiKey.message}</p>}

              {/* API Key Instructions */}
              {watchedProvider === 'hubspot' && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>HubSpot API Key:</strong> Go to Settings → Integrations → API Key in
                    your HubSpot account
                  </p>
                </div>
              )}

              {watchedProvider === 'Capsule' && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Capsule CRM API Key:</strong> Go to My Preferences → API Authentication
                    Token in your Capsule CRM account
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Test Connection Button */}
          {watchedProvider && (
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={testConnection}
                disabled={testing}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {testing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Link className="h-4 w-4 mr-2" />
                )}
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          )}

          {/* Active Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              {...register('isActive')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Enable CRM integration
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <SettingsIcon className="h-4 w-4 mr-2" />
              )}
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Help Section */}
      <div className="mt-6 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">How CRM Integration Works</h3>
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            • When CRM integration is active, an additional "CRM Name" column appears in the
            Customers section
          </p>
          <p>• Customer names are automatically synced from your CRM system</p>
          <p>
            • You can edit the customer name locally while keeping the original CRM name for
            reference
          </p>
          <p>• Only one CRM provider can be active at a time</p>
        </div>
      </div>
    </div>
  );
};

export default CRMSettings;
